import React from 'react';
import { Shield, AlertTriangle, FileText } from 'lucide-react';
import Navbar from '../components/Navbar';

export default function TermsPage() {
  const lastUpdated = "February 2025";

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-[#0F2A44] rounded-2xl flex items-center justify-center mx-auto mb-6">
            <FileText className="h-8 w-8 text-white" />
          </div>
          <h1 className="font-manrope font-bold text-4xl text-[#0F2A44] mb-4">
            Terms and Conditions
          </h1>
          <p className="font-public text-[#4B5563]">
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 space-y-8">
          
          {/* Important Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-manrope font-bold text-lg text-amber-800 mb-2">
                  Important Medical Disclaimer
                </h2>
                <p className="font-public text-amber-700">
                  Afyagram is a healthcare provider discovery and information platform. We do NOT provide medical diagnoses, treatment recommendations, or medical advice. The AI-powered symptom checker is for informational and triage purposes only and should never replace professional medical consultation.
                </p>
              </div>
            </div>
          </div>

          {/* Section 1 */}
          <section>
            <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4">
              1. Acceptance of Terms
            </h2>
            <p className="font-public text-[#4B5563] leading-relaxed">
              By accessing or using Afyagram (the "Platform"), you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services. Your continued use of the Platform constitutes acceptance of any updates or modifications to these terms.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4">
              2. Description of Services
            </h2>
            <p className="font-public text-[#4B5563] leading-relaxed mb-4">
              Afyagram provides the following services:
            </p>
            <ul className="list-disc list-inside space-y-2 font-public text-[#4B5563]">
              <li>Healthcare provider directory and search functionality</li>
              <li>Doctor profiles with verified credentials (KMPDC verification)</li>
              <li>Patient reviews and ratings system</li>
              <li>AI-powered symptom checker for informational triage purposes</li>
              <li>Appointment booking facilitation</li>
              <li>Health information and resources</li>
            </ul>
          </section>

          {/* Section 3 - Medical Disclaimer */}
          <section className="bg-red-50 border border-red-200 rounded-xl p-6">
            <h2 className="font-manrope font-bold text-xl text-red-800 mb-4 flex items-center gap-2">
              <Shield className="h-6 w-6" />
              3. Medical Disclaimer and Limitation of Liability
            </h2>
            <div className="space-y-4 font-public text-red-700">
              <p className="leading-relaxed">
                <strong>3.1 No Medical Advice:</strong> The information provided on Afyagram, including but not limited to the AI symptom checker, health articles, and provider information, is for general informational purposes only. This information is NOT intended to be a substitute for professional medical advice, diagnosis, or treatment.
              </p>
              <p className="leading-relaxed">
                <strong>3.2 AI Symptom Checker Limitations:</strong> Our AI-powered symptom checker is designed to provide general guidance and help direct users to appropriate care. It is NOT a diagnostic tool. The AI cannot examine you physically, access your complete medical history, or perform medical tests. Always consult a qualified healthcare professional for any health concerns.
              </p>
              <p className="leading-relaxed">
                <strong>3.3 Emergency Situations:</strong> If you are experiencing a medical emergency, call emergency services (999 in Kenya) immediately. Do not rely on Afyagram for emergency medical situations.
              </p>
              <p className="leading-relaxed">
                <strong>3.4 No Liability:</strong> Afyagram, its owners, employees, and affiliates shall NOT be liable for any direct, indirect, incidental, consequential, or punitive damages arising from:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Your use of or inability to use the Platform</li>
                <li>Any decisions made based on information provided on the Platform</li>
                <li>Any interactions with healthcare providers found through the Platform</li>
                <li>Accuracy or completeness of AI-generated responses</li>
                <li>Delays in seeking medical attention</li>
                <li>Any health outcomes resulting from use of this Platform</li>
              </ul>
              <p className="leading-relaxed">
                <strong>3.5 Healthcare Provider Responsibility:</strong> Healthcare providers listed on Afyagram are independent professionals. Afyagram does not employ these providers and is not responsible for their actions, advice, treatment, or services.
              </p>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4">
              4. User Responsibilities
            </h2>
            <p className="font-public text-[#4B5563] leading-relaxed mb-4">
              As a user of Afyagram, you agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 font-public text-[#4B5563]">
              <li>Provide accurate and truthful information when using our services</li>
              <li>Not use the Platform for any unlawful purposes</li>
              <li>Not attempt to manipulate reviews or ratings</li>
              <li>Seek professional medical advice for health concerns</li>
              <li>Not share your account credentials with others</li>
              <li>Report any inaccuracies in provider information</li>
              <li>Use the AI symptom checker responsibly and not as a replacement for medical care</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4">
              5. Healthcare Provider Terms
            </h2>
            <p className="font-public text-[#4B5563] leading-relaxed mb-4">
              Healthcare providers using Afyagram agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 font-public text-[#4B5563]">
              <li>Provide accurate professional credentials and KMPDC registration information</li>
              <li>Maintain current and valid medical licenses</li>
              <li>Not misrepresent qualifications, experience, or services</li>
              <li>Respond to patient inquiries in a timely and professional manner</li>
              <li>Comply with all applicable medical regulations and ethical standards</li>
              <li>Maintain patient confidentiality as required by law</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4">
              6. Privacy and Data Protection
            </h2>
            <p className="font-public text-[#4B5563] leading-relaxed">
              Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy. By using Afyagram, you consent to the collection, processing, and storage of your data as described in the Privacy Policy. Health information shared with the AI symptom checker is processed securely and is not shared with third parties without your consent, except as required by law.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4">
              7. Intellectual Property
            </h2>
            <p className="font-public text-[#4B5563] leading-relaxed">
              All content on Afyagram, including text, graphics, logos, and software, is the property of Afyagram or its content suppliers and is protected by copyright and intellectual property laws. You may not reproduce, distribute, or create derivative works from this content without express written permission.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4">
              8. Indemnification
            </h2>
            <p className="font-public text-[#4B5563] leading-relaxed">
              You agree to indemnify and hold harmless Afyagram, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from your use of the Platform, violation of these Terms, or infringement of any third-party rights.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4">
              9. Modifications to Terms
            </h2>
            <p className="font-public text-[#4B5563] leading-relaxed">
              Afyagram reserves the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting on the Platform. Your continued use of the Platform after any changes constitutes acceptance of the modified terms.
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4">
              10. Governing Law
            </h2>
            <p className="font-public text-[#4B5563] leading-relaxed">
              These Terms and Conditions shall be governed by and construed in accordance with the laws of the Republic of Kenya. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of Kenya.
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="font-manrope font-bold text-xl text-[#0F2A44] mb-4">
              11. Contact Information
            </h2>
            <p className="font-public text-[#4B5563] leading-relaxed">
              If you have any questions about these Terms and Conditions, please contact us at:
            </p>
            <div className="mt-4 bg-slate-50 rounded-lg p-4 font-public text-[#4B5563]">
              <p><strong>Email:</strong> legal@afyagram.com</p>
              <p><strong>Website:</strong> www.afyagram.com</p>
            </div>
          </section>

          {/* Acknowledgment */}
          <section className="bg-[#0F2A44] text-white rounded-xl p-6">
            <h2 className="font-manrope font-bold text-xl mb-4">
              Acknowledgment
            </h2>
            <p className="font-public leading-relaxed">
              By using Afyagram, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. You also acknowledge that the Platform is not a substitute for professional medical advice, and you assume full responsibility for how you use the information and services provided.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}
